// apolo/src/components/code/sessionWorkspace/codeRouting.js — P5-T01/T02 (code-tab-lift).
//
// PURE model→route resolution extracted from SessionWorkspace (Ousterhout: shrink the
// shell's visible complexity). detectRoute maps a selected model + the account's
// adapters to a { kind:'local'|'cloud', … } dispatch descriptor; buildAvailableModels
// reads the registry ONCE here (the composition root) so the turn-router domain stays
// registry-free. No React, no component state — unit-testable in isolation.
import { isLocalAdapter } from '../../../hooks/useUnifiedModels.js';
import { MODEL_REGISTRY } from '../../../lib/modelRegistry.js';
import { reasoningEffortForTurn } from '../../../lib/reasoningEffort.js';

// G4-WS1-T03 — the model-selector parse (the `adapter:<id>:<model>` prefix + adapter
// lookup) lifted out of detectRoute so the resolver stays under the complexity ceiling.
// Returns { modelName, adapter }. Pure.
function resolveModelAdapter(model, list) {
  let modelName = model || '';
  let adapterId = null;
  if (modelName.startsWith('adapter:')) {
    const rest = modelName.slice('adapter:'.length);
    adapterId = rest.split(':')[0];
    modelName = rest.includes(':') ? rest.split(':').slice(1).join(':') : '';
  }
  let adapter = adapterId ? list.find((a) => a.id === adapterId) : null;
  if (!adapter && modelName) {
    adapter = list.find((a) => (a.available_models || []).includes(modelName));
  }
  return { modelName, adapter };
}

// The LOCAL dispatch descriptor for a chosen local adapter (Ollama vs LM Studio detected
// from the adapter blob; defaults per engine). Pure.
function buildLocalRoute(localAdapter, modelName) {
  const blob = ((localAdapter.name || '') + (localAdapter.display_name || '') + (localAdapter.base_url || '')).toLowerCase();
  const isLm = blob.includes('lmstudio') || blob.includes('lm studio') || blob.includes('1234');
  return {
    kind: 'local',
    baseUrl: localAdapter.base_url || (isLm ? 'http://localhost:1234/v1' : 'http://127.0.0.1:11434'),
    model: modelName || localAdapter.default_model || (isLm ? '' : 'qwen2.5:7b'),
    isLmStudio: isLm,
  };
}

export function detectRoute(model, adapters, uiEffort) {
  const list = adapters || [];
  const { modelName, adapter } = resolveModelAdapter(model, list);

  // LOCAL: adapter local explícito OU fallback (model sem "/" + adapter local ativo)
  const localActive = list.find((a) => isLocalAdapter(a) && a.is_active !== false);
  let localAdapter = null;
  if (adapter && isLocalAdapter(adapter)) localAdapter = adapter;
  else if (!adapter && localActive && modelName && !modelName.includes('/')) localAdapter = localActive;

  if (localAdapter) return buildLocalRoute(localAdapter, modelName);

  // CLOUD: registry model (anthropic/openai/...) OU custom adapter (Groq via id).
  // Speed selector → per-turn reasoning_effort, gated to reasoning models on a provider
  // that forwards it (null otherwise → param omitted, unchanged behavior).
  return {
    kind: 'cloud',
    model: modelName || model,
    llmAdapterId: (adapter && !isLocalAdapter(adapter)) ? adapter.id : null,
    reasoningEffort: reasoningEffortForTurn(modelName || model, uiEffort),
  };
}

// Turn Role Router — the account's resolvable models as { id, score } (score = a
// price proxy, lower = cheaper) for the PURE resolveRoutedModel. Reading the
// registry happens ONLY here at the composition root; the domain stays registry-free.
export function buildAvailableModels(adapters) {
  const ids = new Set();
  for (const a of adapters || []) {
    if (a?.is_active === false || a?.is_active === 0) continue;
    for (const m of a?.available_models || []) ids.add(String(m).toLowerCase());
  }
  // Auditoria 07-06: adapters guardam nomes SEM prefixo de provider
  // ('gemini-2.0-flash') e o registry usa id prefixado ('google/gemini-2.5-flash')
  // — a interseção era sempre VAZIA e o downgrade do router nunca disparava.
  // Casa com e sem o prefixo, case-insensitive.
  return MODEL_REGISTRY
    .filter((m) => {
      const id = String(m.id).toLowerCase();
      return ids.has(id) || ids.has(id.split('/').slice(1).join('/'));
    })
    .map((m) => ({ id: m.id, provider: m.provider, capabilities: m.capabilities, contextWindow: m.contextWindow, score: (m.pricing?.input || 0) + (m.pricing?.output || 0) }));
}
